import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { all_routes } from '../../../router/all_routes'
import ImageWithBasePath from '../../../../core/common/imageWithBasePath'
import Table from "../../../../core/common/dataTable/index";
import TeacherModal from "../teacherModal";
import { RootState } from "../../../../core/data/redux/store";
import TooltipOption from "../../../../core/common/tooltipOption";
import { useDispatch, useSelector } from "react-redux";
import { fetchteachersSuccess } from "../../../../core/data/redux/teacherSlice";
import {
	deleteTeacherApi,
	fetchTeachersApi,
} from "../../../../core/data/services/teacherService";
import { Teacher } from "../../../../core/data/types/teacher";
import {
	exportToPDF,
	exportToExcel,
	ExportColumn,
} from "../../../../utils/exportUtils";
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';
import CommonSelect, { Option } from '../../../../core/common/commonSelect';

// Define the filter interface
interface TeacherFilters {
	name: string;
	designation: string;
	email: string;
	phone: string;
	// Add more filter parameters as needed
}

// Initial filter state
const initialFilters: TeacherFilters = {
	name: '',
	designation: '',
	email: '',
	phone: '',
};

const TeacherGrid = () => {
	const routes = all_routes;

	const hasFetched = useRef(false);
	const dispatch = useDispatch();
	const [deleteTeacherId, setDeleteTeacherId] = useState<number | null>(null);
	const [viewType,] = useState<"grid" | "list">("list");
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

	// Single filter state for all parameters
	const [filters, setFilters] = useState<TeacherFilters>(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState<TeacherFilters>(initialFilters);
	const [filteredData, setFilteredData] = useState<Teacher[]>([]);

	// Error and Success modal states
	const [error, setError] = useState<string>("");
	const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
	const [successMessage, setSuccessMessage] = useState<string>("");
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

	useEffect(() => {
		if (hasFetched.current) return;
		hasFetched.current = true;
		const fetchData = async () => {
			try {
				const addedteacher = await fetchTeachersApi();
				dispatch(fetchteachersSuccess(addedteacher));
			} catch (error: any) {
				const msg = error?.message || "Failed to fetch teachers.";
				setError(msg);
				setShowErrorModal(true);
				console.error("Error fetching teachers:", msg);
			}
		};
		fetchData();
	}, [dispatch]);

	// Fetch teachers data from the Redux store
	const teachers = useSelector((state: RootState) => state.teachers.teachers);

	// Initialize filtered data when teachers change
	useEffect(() => {
		setFilteredData(teachers);
	}, [teachers]);

	// Generic filter update function
	const updateFilter = (key: keyof TeacherFilters, value: string) => {
		setFilters(prev => ({
			...prev,
			[key]: value
		}));
	};

	// Generate dynamic filter options from actual data
	const getDynamicFilterOptions = () => {
		const nameOptions = [
			{ value: '', label: 'All Names' },
			...Array.from(new Set(teachers.map((teacher: any) =>
				`${ teacher.firstName } ${ teacher.lastName }`
			))).map(name => ({ value: name, label: name }))
		];

		const designationOptions = [
			{ value: '', label: 'All Designations' },
			...Array.from(new Set(teachers.map((teacher: any) =>
				teacher.designation
			).filter(Boolean))).map(designation => ({
				value: designation,
				label: designation
			}))
		];

		const emailOptions = [
			{ value: '', label: 'All Emails' },
			...Array.from(new Set(teachers.map((teacher: any) =>
				teacher.email
			).filter(Boolean))).map(email => ({
				value: email,
				label: email
			}))
		];

		return { nameOptions, designationOptions, emailOptions };
	};

	// Apply filters based on applied filter states
	// Apply filters based on applied filter states
	const applyFilters = useCallback((filtersToApply: TeacherFilters = appliedFilters) => {
		let filtered = teachers;

		// Filter by name (partial match, case insensitive)
		if (filtersToApply.name) {
			filtered = filtered.filter((teacher: any) => {
				const fullName = `${ teacher.firstName } ${ teacher.lastName }`;
				return fullName.toLowerCase().includes(filtersToApply.name.toLowerCase());
			});
		}

		// Filter by designation
		if (filtersToApply.designation) {
			filtered = filtered.filter((teacher: any) =>
				teacher.designation === filtersToApply.designation
			);
		}

		// Filter by email (partial match, case insensitive)
		if (filtersToApply.email) {
			filtered = filtered.filter((teacher: any) =>
				teacher.email.toLowerCase().includes(filtersToApply.email.toLowerCase())
			);
		}

		// Filter by phone (partial match)
		if (filtersToApply.phone) {
			filtered = filtered.filter((teacher: any) =>
				teacher.phone.includes(filtersToApply.phone)
			);
		}

		setFilteredData(filtered);
	}, [teachers, appliedFilters]);

	// Reset filters
	const resetFilters = () => {
		setFilters(initialFilters);
		setAppliedFilters(initialFilters);
		setFilteredData(teachers);
	};

	// Check if any filters are applied
	const hasActiveFilters = useCallback(() => {
		return Object.values(appliedFilters).some(value => value !== '');
	}, [appliedFilters]);

	// Get count of active filters
	const getActiveFilterCount = () => {
		return Object.values(appliedFilters).filter(value => value !== '').length;
	};

	// Only apply filters when teachers data changes (initial load or data updates)
	useEffect(() => {
		if (!hasActiveFilters()) {
			setFilteredData(teachers);
		} else {
			applyFilters();
		}
	}, [teachers, applyFilters, hasActiveFilters]);

	// Extract the data array from teachers
	// const tableData = filteredData;

	// Handle Exports
	const exportColumns: ExportColumn[] = [
		{ title: "ID", dataIndex: "id" },
		{
			title: "Full Name",
			dataIndex: "firstName",
			render: (_, row) => `${ row.firstName } ${ row.lastName }`,
		},
		{ title: "Designation", dataIndex: "designation" },
		{ title: "Email", dataIndex: "email" },
		{ title: "Phone", dataIndex: "phone" },
	];

	const handleExportPDF = () =>
		exportToPDF(filteredData, exportColumns, "Teachers.pdf");
	const handleExportExcel = () =>
		exportToExcel(filteredData, exportColumns, "Teachers.xlsx");

	// Handler to confirm delete
	const handleConfirmDelete = async () => {
		if (deleteTeacherId == null) return;
		try {
			await deleteTeacherApi(deleteTeacherId);
			// Refetch teachers after delete
			const updatedTeachers = await fetchTeachersApi();
			dispatch(fetchteachersSuccess(updatedTeachers));

			// Show success modal
			setSuccessMessage("Teacher deleted successfully.");
			setShowSuccessModal(true);
		} catch (error: any) {
			const msg = error?.message || "An unknown error occurred during deletion.";
			console.error("Error deleting teacher:", msg);
			setError(msg);
			setShowErrorModal(true);
		} finally {
			setDeleteTeacherId(null);
		}
	};

	// Modal handlers
	const handleCloseErrorModal = () => {
		setShowErrorModal(false);
		setError("");
	};

	const handleCloseSuccessModal = () => {
		setShowSuccessModal(false);
		setSuccessMessage("");
	};

	const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

	const columns = [
		{
			title: "Teacher Name & Designation",
			dataIndex: "firstName",
			render: (text: string, record: any) => (
				<div className="d-flex align-items-center">
					<div className="avatar avatar-md">
						<ImageWithBasePath
							src={record.profileImage ?? "assets/img/teachers/teacher-01.jpg"}
							className="img-fluid rounded-circle"
							alt="img"
						/>
					</div>
					<div className="user-detail ms-2">
						<p className="text-dark mb-0">{text} {record.lastName}</p>
						<p className="mb-0">{record.designation}</p>
					</div>
				</div>
			),
			sorter: (a: any, b: any) => a.firstName.localeCompare(b.firstName),
		},
		{
			title: "Unique ID",
			dataIndex: "id",
			render: (_: any, record: any) => <span>{"UNIID" + record.id}</span>,
			sorter: (a: any, b: any) => a.id - b.id,
		},
		{
			title: "Email",
			dataIndex: "email",
			sorter: (a: any, b: any) => a.email.localeCompare(b.email),
		},
		{
			title: "Phone",
			dataIndex: "phone",
			sorter: (a: any, b: any) => a.phone.localeCompare(b.phone),
		},
		{
			title: "Action",
			dataIndex: "action",
			render: (_: any, record: Teacher) => (
				<div className="dropdown">
					<Link
						to="#"
						className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
						data-bs-toggle="dropdown"
						aria-expanded="false"
					>
						<i className="ti ti-dots-vertical fs-14" />
					</Link>
					<ul className="dropdown-menu dropdown-menu-right p-3">
						<li>
							<Link
								className="dropdown-item rounded-1"
								to={routes.teacherDetails.replace(":id", record.id?.toString())}
								state={{ teacher: record }}
							>
								<i className="ti ti-menu me-2" />View Teacher
							</Link>
						</li>
						<li>
							<Link
								className="dropdown-item rounded-1"
								to={routes.editTeacher.replace(":id", record.id?.toString())}
								state={{ teacher: record }}
							>
								<i className="ti ti-edit-circle me-2" />Edit
							</Link>
						</li>
						<li>
							<Link
								className="dropdown-item rounded-1"
								to="#"
								data-bs-toggle="modal"
								data-bs-target="#delete-modal"
								onClick={() => setDeleteTeacherId(record.id)}
							>
								<i className="ti ti-trash-x me-2" />Delete
							</Link>
						</li>
					</ul>
				</div>
			),
		},
	];

	const handleApplyClick = () => {
		// Update applied filter states when Apply button is clicked
		setAppliedFilters(filters);

		// Apply filters immediately
		applyFilters(filters);

		// Close the dropdown
		if (dropdownMenuRef.current) {
			dropdownMenuRef.current.classList.remove("show");
		}
	};

	// Get dynamic options
	const { designationOptions } = getDynamicFilterOptions();
	const designationOpts: Option[] = Array.isArray(designationOptions)
		? (designationOptions as Option[])
		: [];

	// derive current option from string filter
	const currentDesignation: Option | null =
		filters.designation
			? designationOpts.find(o => String(o.value) === String(filters.designation)) ?? null
			: null;

	return (
		<>
			{/* Page Wrapper */}
			<div className="page-wrapper">
				<div className="content content-two">
					{/* Page Header */}
					<div className="d-md-flex d-block align-items-center justify-content-between mb-3">
						<div className="my-auto mb-2">
							<h3 className="page-title mb-1">Teachers</h3>
							<nav>
								<ol className="breadcrumb mb-0">
									<li className="breadcrumb-item">
										<Link to={routes.HeadmasterDashboard}>Dashboard</Link>
									</li>
									<li className="breadcrumb-item active" aria-current="page">
										Teachers
									</li>
								</ol>
							</nav>
						</div>
						<div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
							<TooltipOption
								onExportPDF={handleExportPDF}
								onExportExcel={handleExportExcel}
							/>

							<div className="mb-2">
								<Link
									to={routes.addTeacher}
									className="btn btn-primary d-flex align-items-center"
								>
									<i className="ti ti-square-rounded-plus me-2" />
									Add Teacher
								</Link>
							</div>
						</div>
					</div>

					<div className="card">
						<div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
							<h4 className="mb-3">Teachers</h4>
							<div className="d-flex align-items-center flex-wrap">
								<div className="dropdown mb-3 me-2">
									<Link
										to="#"
										className="btn btn-outline-light bg-white dropdown-toggle"
										data-bs-toggle="dropdown"
										data-bs-auto-close="outside"
									>
										<i className="ti ti-filter me-2" />
										Filter
										{hasActiveFilters() && (
											<span className="badge badge-primary ms-1">
												{getActiveFilterCount()}
											</span>
										)}
									</Link>
									<div
										className="dropdown-menu drop-width"
										ref={dropdownMenuRef}
									>
										<form>
											<div className="d-flex align-items-center border-bottom p-3">
												<h4>Filter Teachers</h4>
											</div>
											<div className="p-3 pb-0 border-bottom">
												<div className="row">
													<div className="col-md-6">
														<div className="mb-3">
															<label className="form-label">Name</label>
															<input
																type="text"
																className="form-control"
																placeholder="Enter teacher name..."
																value={filters.name}
																onChange={(e) => updateFilter('name', e.target.value)}
															/>
														</div>
													</div>
													<div className="col-md-6">
														<div className="mb-3">
															<label className="form-label">Designation</label>
															
															<CommonSelect
																options={designationOpts}
																defaultValue={currentDesignation}
																onChange={(opt) => updateFilter("designation", opt?.value ?? "")}
															/>
														</div>
													</div>
													<div className="col-md-6">
														<div className="mb-3">
															<label className="form-label">Email</label>
															<input
																type="text"
																className="form-control"
																placeholder="Enter email..."
																value={filters.email}
																onChange={(e) => updateFilter('email', e.target.value)}
															/>
														</div>
													</div>
													<div className="col-md-6">
														<div className="mb-3">
															<label className="form-label">Phone</label>
															<input
																type="text"
																className="form-control"
																placeholder="Enter phone number..."
																value={filters.phone}
																onChange={(e) => updateFilter('phone', e.target.value)}
															/>
														</div>
													</div>
												</div>
											</div>
											<div className="p-3 d-flex align-items-center justify-content-between">
												<div className="text-muted small">
													Showing {filteredData.length} of {teachers.length} teachers
												</div>
												<div>
													<button
														type="button"
														onClick={resetFilters}
														className="btn btn-light me-3"
													>
														Reset
													</button>
													<button
														type="button"
														onClick={handleApplyClick}
														className="btn btn-primary"
													>
														Apply
													</button>
												</div>
											</div>
										</form>
									</div>
								</div>
							</div>
						</div>

						<div className="">
							{viewType === "list" && (
								<div className="card-body p-0 py-3">
									<Table
										dataSource={filteredData}
										columns={columns}
										Selection={true}
										selectedRowKeys={selectedRowKeys}
										setSelectedRowKeys={setSelectedRowKeys}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{/* /Page Wrapper */}

			{/* Delete confirmation modal */}
			<TeacherModal onConfirmDelete={handleConfirmDelete} />

			{/* Error Modal */}
			<ErrorModal
				show={showErrorModal}
				message={error}
				onClose={handleCloseErrorModal}
			/>

			{/* Success Modal */}
			<SuccessModal
				show={showSuccessModal}
				message={successMessage}
				onClose={handleCloseSuccessModal}
			/>
		</>
	);
};

export default TeacherGrid;

